/**
 * v6 migration notes — unsaved-changes guard downgrade (per direction):
 *
 *  - `useHistory()` -> `useNavigate()`. `history.push(x)` -> `navigate(x)`.
 *
 *  - `history.block()` is REMOVED ENTIRELY in v6's stable API (it only
 *    exists again under a data router via `useBlocker`, which this app
 *    doesn't use — see App.js). That mechanism intercepted navigation
 *    triggered from ANYWHERE in the app: sidebar links, header nav, browser
 *    back/forward, programmatic redirects elsewhere in the codebase. There
 *    is no drop-in replacement for that breadth of coverage without
 *    migrating to createBrowserRouter.
 *
 *    Per direction, this has been downgraded to the lighter option used
 *    elsewhere in this migration (MyBusinessAccount):
 *      - `beforeunload` still fully covers tab close / refresh / closing
 *        the browser — unaffected by this change.
 *      - In-app navigation interception is GONE. Concretely, this means:
 *          • Clicking a sidebar/header nav link while `isDirty` is true
 *            will navigate away with NO warning and NO confirmation.
 *          • The browser's back/forward buttons are not intercepted either.
 *      - This component itself has no "leave" button of its own (the
 *        Reset button intentionally clears the draft — that's its actual
 *        purpose, not a navigation event — and step transitions are local
 *        `setStep` calls, not route changes), so there was nothing else in
 *        this file to wire a confirm-before-navigate check onto.
 *
 *    `UnsavedChangesModal`, `showExitModal`, and `pendingTransitionRef` are
 *    kept in place (now effectively unreachable — nothing sets
 *    `showExitModal` true anymore) rather than deleted, since they're the
 *    obvious hook point if/when sidebar-link interception or the
 *    data-router migration happens. If that's not wanted, this whole block
 *    can be removed; flagging here rather than silently leaving dead code
 *    unexplained.
 *
 *    Revisit this if in-app navigation away from an in-progress coupon
 *    turns out to be a real problem in practice — the data-router
 *    migration is the correct fix when that's worth the larger change.
 */

import { Field, Form } from 'react-final-form';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { toast } from 'react-toastify';

import { Button, FormRadio, Modal } from 'components';
import PaymentModal from 'components/PaymentModal/PaymentModal';
import SendCouponNotificationModal from 'components/SendCouponNotificationModal/SendCouponNotificationModal';
import AuthProtected from 'containers/AuthProtected/AuthProtected';
import {
  actions as cbActions,
  duck as cbDuck,
} from 'coupon-common/src/modules/couponBuilder';
import { actions as imgActions } from 'coupon-common/src/modules/couponImages';
import { actions as bizActions } from 'coupon-common/src/modules/businessInfo';
import { dispatchAsAsync } from 'coupon-common/src/utils';
import { COUPON_STATES } from 'coupon-common';

import { actions as followerActions } from 'coupon-common/src/modules/followers/duck';
import { actions as notificationActions } from 'coupon-common/src/modules/sendNotification/duck';
// Page 6 is removed
import { getDefaultTaxes } from 'utils/taxDefaults';

import Page1_Goals from './Page1_Goals';
import Page2_BuildYourDeal from './Page2_BuildYourDeal';
import Page3_OfferImpact from './Page3_OfferImpact';
import Page4_RecommendedCoupons from './Page4_RecommendedCoupons';
import Page5_DesignCoupon from './Page5_DesignCoupon';
import { products, sendNotification } from './utils/sendNotification';

const LOCAL_STORAGE_KEY_BASE = 'coupon_builder_v2_data';
const LOCAL_STORAGE_KEY = LOCAL_STORAGE_KEY_BASE;
const COUPON_TECHNICAL_KEY = 'newCouponID';
const TOTAL_STEPS = 5;

// ─── All localStorage keys this builder and its children ever write ──────────
// Keeping this list centralised means cleanup is always complete, even when
// child pages (Page2, Page3) write their own keys directly.
const ALL_WIZARD_STORAGE_KEYS = [
  LOCAL_STORAGE_KEY_BASE,
  'cbv2_editMode',
  'cbv2_editCouponId',
  'cbv2_forceStep',
  'cbv2_offerImpactKey',
  'cbv2_speed',
  'cbv2_itemName',
  'cbv2_itemPrice',
  'cbv2_itemDesc',
  'cbv2_savedTaxes',
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const clearAllWizardStorage = (storageKey) => {
  try {
    ALL_WIZARD_STORAGE_KEYS.forEach(k => localStorage.removeItem(k));
    // Also clear the business-scoped key passed in
    if (storageKey) localStorage.removeItem(storageKey);
  } catch { }
};

const dataUrlToBlob = (dataUrl) => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const normalizeImpactToSpeed = key => {
  if (!key) return 'Steady';
  const k = String(key).toLowerCase().trim();
  if (['steady', 'steady_growth', 'balanced', '1'].includes(k)) return 'Steady';
  if (['quick', 'quick_boost', 'accelerated', '2'].includes(k)) return 'Quick';
  if (['max', 'maximum_impact', 'aggressive', '3'].includes(k)) return 'Max';
  return 'Steady';
};

const scopeFromItemType = itemType =>
  itemType === 'individual'
    ? 'Individual Item'
    : itemType === 'category'
      ? 'Product Line'
      : 'Storewide Promotion';

// Normalize any goal label variants from Page 1 into Page 4's compact set.
const normalizeGoalForP4 = goalKey => {
  if (!goalKey) return 'No Goal';
  const g = String(goalKey).toLowerCase();
  if (g.includes('more customers') || g.includes('attract')) return 'New Customers';
  if (g.includes('sell')) return 'Sell More';
  if (g.includes('repeat')) return 'Repeat Business';
  if (g.includes('slow') || g.includes('off-peak')) return 'Slow Periods';
  if (g.includes('special')) return 'Special Promotion';
  return 'No Goal';
};

// add near the other helpers in CouponBuilderV1.js
const PROVINCE_BY_ID = {
  1: 'ON', 2: 'QC', 3: 'NS', 4: 'NB', 5: 'MB', 6: 'BC',
  7: 'PE', 8: 'SK', 9: 'AB', 10: 'NL', 11: 'NT', 12: 'NU', 13: 'YT',
};

const normProv = p => {
  if (!p) return 'NS';
  if (typeof p === 'string') return p;                 // already an abbreviation, e.g. "ON"
  if (typeof p === 'object') {
    return p.abbreviation || p.code || PROVINCE_BY_ID[p.id] || 'NS';
  }
  return PROVINCE_BY_ID[p] || 'NS';                     // raw numeric id
};

// ─── Unsaved Changes Modal ────────────────────────────────────────────────────
//
// Intentionally self-contained with inline styles so it has zero dependency on
// external CSS modules and works regardless of which step the user is on.
//
// NOTE: kept for future use (see header comment) — nothing currently sets
// showExitModal to true, since the history.block() mechanism that used to
// trigger it was removed under v6. Wire this back up if/when sidebar-link
// interception or a data-router migration happens.

const UnsavedChangesModal = ({ isOpen, onStay, onLeave }) => {
  // Trap focus inside modal while open
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="unsaved-modal-title"
      aria-describedby="unsaved-modal-desc"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
      }}
      // Clicking the backdrop counts as "Stay"
      onMouseDown={e => { if (e.target === e.currentTarget) onStay(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        onKeyDown={e => { if (e.key === 'Escape') onStay(); }}
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '32px 28px',
          width: '100%',
          maxWidth: '420px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          outline: 'none',
        }}
      >
        <h2
          id="unsaved-modal-title"
          style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: '#111' }}
        >
          Unsaved changes
        </h2>
        <p
          id="unsaved-modal-desc"
          style={{ margin: '0 0 28px', fontSize: '15px', color: '#555', lineHeight: 1.5 }}
        >
          You have unsaved work in your coupon. If you leave now, all progress
          will be lost.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onStay}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              background: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              color: '#374151',
            }}
          >
            Stay
          </button>
          <button
            type="button"
            onClick={onLeave}
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              background: '#ef4444',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Leave & discard
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const CouponBuilderV1 = ({ user, startAtStep, isEditMode, editCouponId }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const { id: routeCouponId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const effectiveEditCouponId = editCouponId || routeCouponId || null;
  const searchParams = new URLSearchParams(location.search);
  const urlMode = (searchParams.get('mode') || '').toLowerCase();
  // const PUSH_NOTIFICATIONS_PROD_CODE = 'PUSH_NOTIFICATION'
  const effectiveIsEditMode =
    typeof isEditMode === 'boolean'
      ? isEditMode
      : urlMode === 'edit' && !!effectiveEditCouponId;

  const businessInfo = useSelector(s => s.businessInfo?.data);
  const businessKey =
    businessInfo?.id || businessInfo?.business_id || user?.id || 'unknown';

  const followerState = useSelector(s => s.followers || { fetched: false, data: null, error: null });

  const STORAGE_KEY = `${LOCAL_STORAGE_KEY_BASE}:${businessKey}`;
  // Session marker: present only while this tab is alive.
  // If the user closed the tab mid-flow, sessionStorage will have been wiped
  // by the browser, so on the next visit we know the localStorage data is stale.
  const SESSION_ACTIVE_KEY = `${STORAGE_KEY}:active`;

  const couponImages = useSelector(s => s.couponImages || {});
  const couponBuilderState = useSelector(s => s.couponBuilder || {});
  const couponEntry =
    (effectiveEditCouponId && couponBuilderState[effectiveEditCouponId]) ||
    cbDuck.INITIAL_COUPON_ITEM_STATE;

  const { data: couponData } = couponEntry;

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});

  // ── Unsaved-changes guard state ───────────────────────────────────────────
  // isDirty / showExitModal / pendingTransitionRef are kept for the
  // beforeunload guard + as the hook point for a future fuller fix (see
  // header comment). The history.block-driven interception is gone.
  const [isDirty, setIsDirty] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const pendingTransitionRef = useRef(null);
  const unblockRef = useRef(null);

  const isFormDataReady =
    !effectiveIsEditMode ||
    (effectiveIsEditMode && !!couponData && !!couponData.id);

  // ── Normalise serialised fields back to runtime types ────────────────────
  const normalizeWizard = obj => {
    const out = { ...(obj || {}) };
    if (out.expiryDate) {
      const d = new Date(out.expiryDate);
      out.expiryDate = Number.isNaN(d.getTime()) ? null : d;
    }
    return out;
  };

  // ── saveWizard: single function that persists + marks the session dirty ──
  const saveWizard = useCallback(data => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      sessionStorage.setItem(SESSION_ACTIVE_KEY, '1');
    } catch { }
    setIsDirty(true);
    // STORAGE_KEY / SESSION_ACTIVE_KEY are derived from businessKey which is
    // stable after mount, so the dep array below is intentionally static.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [STORAGE_KEY, SESSION_ACTIVE_KEY]);

  // ── Hydration: restore saved wizard state (or clear stale data) ──────────
  useEffect(() => {
    if (!businessKey || businessKey === 'unknown') return;

    const sessionActive = sessionStorage.getItem(SESSION_ACTIVE_KEY);
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored && !sessionActive) {
      // The tab was closed mid-flow — the saved data was never intentionally
      // saved/published. Wipe it so the next visit starts fresh.
      clearAllWizardStorage(STORAGE_KEY);
      setFormData({});
    } else if (stored) {
      try {
        setFormData(normalizeWizard(JSON.parse(stored)));
      } catch {
        clearAllWizardStorage(STORAGE_KEY);
        setFormData({});
      }
    } else {        
      setFormData({});
    }

    // Mark this tab session as active (cleared automatically on tab close).
    sessionStorage.setItem(SESSION_ACTIVE_KEY, '1');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessKey]);

  // ── Block in-app navigation when there is unsaved work ───────────────────
  useEffect(() => {
    if (!isDirty) return;

    // React Router v5: history.block receives the target Location + Action.
    // Returning a string (or false) prevents the transition. We store the
    // "confirm" callback so our modal can call it later.
    unblockRef.current = navigate((targetLocation, action) => {
      // Allow internal step navigation — these are not real route changes.
      // The builder stays on the same route; only `step` state changes.
      // We detect "real" outbound navigation by checking if the pathname differs.
      if (targetLocation.pathname === location.pathname) return undefined;

      pendingTransitionRef.current = () => {
        if (unblockRef.current) {
          unblockRef.current(); // remove the block before retrying
          unblockRef.current = null;
        }
        navigate(targetLocation.pathname + (targetLocation.search || ''));
      };
      setShowExitModal(true);
      return false; // block the navigation
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isDirty, navigate, location.pathname]);

  // ── Block browser tab close / page refresh when there is unsaved work ────
  // This is the one piece of the original guard that survives unchanged —
  // beforeunload is a plain browser API, unrelated to react-router.
  useEffect(() => {
    if (!isDirty) return;
    const handler = e => {
      e.preventDefault();
      e.returnValue = ''; // Required for Chrome to show the native dialog
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Modal action handlers ─────────────────────────────────────────────────
  // Kept for the future hook-up described in the header comment.
  const handleStay = () => {
    pendingTransitionRef.current = null;
    setShowExitModal(false);
  };

  const handleLeave = () => {
    // Clear all storage so the next visit starts fresh
    clearAllWizardStorage(STORAGE_KEY);
    try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch { }
    setIsDirty(false);
    setShowExitModal(false);

    if (pendingTransitionRef.current) {
      pendingTransitionRef.current();
      pendingTransitionRef.current = null;
    }
  };

  // ── Helper: mark wizard as clean and wipe all storage after success ───────
  const finalizeWizard = () => {
    clearAllWizardStorage(STORAGE_KEY);
    try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch { }
    setIsDirty(false);
  };

  // ── Cleanup legacy unscoped key ───────────────────────────────────────────
  useEffect(() => {
    try { localStorage.removeItem('coupon_builder_v2_data'); } catch { }
  }, []);

  // ── Load business info ────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(bizActions.getInfo(COUPON_TECHNICAL_KEY));
  }, [dispatch]);

  // ── Initialise coupon image slots ─────────────────────────────────────────
  useEffect(() => {
    const slots =
      (businessInfo?.coupon_image_slot && Number(businessInfo.coupon_image_slot)) ||
      (user?.is_premium ? 3 : 1);
    if (slots) {
      const initialImagesPayload =
        effectiveIsEditMode && couponData ? couponData : {};
      dispatch(imgActions.loadCouponImagesState(slots, initialImagesPayload));
    }
  }, [dispatch, businessInfo, user?.is_premium, effectiveIsEditMode, couponData]);

  // ── Start step logic ──────────────────────────────────────────────────────
  useEffect(() => {
    let initial = 0;
    if (typeof startAtStep === 'number' && startAtStep > 0) {
      initial = startAtStep;
    }
    if (!initial) {
      const params = new URLSearchParams(location.search);
      const mode = (params.get('mode') || '').toLowerCase();
      const urlStep = Number(params.get('step') || 0);
      if (mode === 'edit' && urlStep > 0) initial = urlStep;
    }
    if (!initial) {
      const forced = Number(localStorage.getItem('cbv2_forceStep') || 0);
      if (forced > 0) initial = forced;
    }
    if (initial > 0) setStep(initial);
    try { localStorage.removeItem('cbv2_forceStep'); } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch coupon data in edit mode ────────────────────────────────────────
  useEffect(() => {
    if (!effectiveIsEditMode || !effectiveEditCouponId) return;
    dispatch(cbActions.getCoupon(effectiveEditCouponId));
  }, [dispatch, effectiveIsEditMode, effectiveEditCouponId]);

  // ── Prefill form data on edit ─────────────────────────────────────────────
  useEffect(() => {
    if (!effectiveIsEditMode || !effectiveEditCouponId) return;
    if (!couponData || !couponData.id) return;

    localStorage.removeItem(LOCAL_STORAGE_KEY);
    const c = couponData;

    const prefill = {
      title: c.title || '',
      description: c.description || '',
      couponType:
        c.type === 'percentage_off' ? 'percentage' : c.type === 'amount_off' ? 'dollar' : 'points',
      discountValue:
        c.type === 'percentage_off'
          ? Number(c.percentage_off || 0)
          : c.type === 'amount_off'
            ? Number(c.amount_off || 0)
            : Number(c.points_back || 0),
      productPrice: c.price ?? c.product_price ?? c.product?.price ?? '',
      itemName: c.item_name || '',
      itemDescription: c.item_description || '',
      productTitle: c.item_name || '',
      productDescription: c.item_description || '',
      itemPrice: c.price ?? c.product_price ?? c.product?.price ?? '',
      itemType: c.item_type === 'all' ? 'storewide' : c.item_type || 'individual',
      expiryType: c.expiry_type === 'amount_limit' ? 'quantity' : 'date',
      totalQuantity: c.expiration_quantity || '',
      exclusiveTo: c.is_exclusive_paid
        ? 'premium'
        : c.is_exclusive_followers
          ? 'followers'
          : '',
      customerType: c.is_exclusive_paid
        ? ['premium']
        : c.is_exclusive_followers
          ? ['followers']
          : [],
      perUserLimitType:
        c.user_limit === 1
          ? 'oneTime'
          : c.user_limit > 1
            ? 'multiple'
            : 'unlimited',
      perUserLimitCount: c.user_limit > 1 ? c.user_limit : '',
      redemptionType: c.auto_accept ? 'auto' : 'manual',
      redeemableWithPoints: c.payment_by_points || false,
      reservationHours: c.reserved_hours || 4,
      terms: c.terms_and_conditions || c.terms || '',
      category: c.category || null,
      // BUG-FIX: the coupons list/detail API returns subcategories as full
      // nested objects ({ id, name, icon_name, category }), but
      // CouponCategoryField's selection check does
      // `selected.includes(sub.id)` against a flat array of ids — and the
      // create/edit payload we send back out is always flat ids too (see
      // buildPayload in Page5/Page4). Normalize here so both sides agree.
      subcategories: Array.isArray(c.subcategories)
        ? c.subcategories
            .map(s => (s && typeof s === 'object') ? (s.id ?? s.value) : s)
            .filter(v => v !== null && v !== undefined)
        : [],
      heroImagePreview: c.image_url || '',
    };

    setFormData(prev => ({ ...prev, ...prefill }));
  }, [effectiveIsEditMode, effectiveEditCouponId, couponData]);

  const safeMerge = (prev, next) => normalizeWizard({ ...prev, ...next });

  // ── Collect S3 image IDs ──────────────────────────────────────────────────
  const collectCouponImageIds = () =>
    Object.keys(couponImages)
      .map(k => (couponImages[k]?.s3_id > 0 ? couponImages[k].s3_id : null))
      .filter(Boolean);

  const formatErrorMessage = errorObj => {
    if (!errorObj || typeof errorObj !== 'object') {
      return 'An unexpected error occurred.';
    }
    const parts = [];
    for (const [field, errs] of Object.entries(errorObj)) {
      if (Array.isArray(errs)) {
        errs.forEach(msg => {
          parts.push(`${field === 'non_field_errors' ? 'Error' : field}: ${msg}`);
        });
      } else {
        parts.push(`${field}: ${errs}`);
      }
    }
    return parts.join('\n');
  };

  const paymentResolverRef = useRef(null);

  const confirmPayment = () => {
    setIsModalOpen(true);
    return new Promise((resolve, reject) => {

      // if(isModalOpen){
      //     resolve(true)
      // }
      // else{
      //   resolve(false)
      // }
      paymentResolverRef.current = resolve;
    })
  }
  const [sendNotificationData, setSendNotificationData] = useState(null);

  // ── Final submit ──────────────────────────────────────────────────────────
  const handleFinalSubmit = async (incomingPayload, isDraft = false) => {


    // const confirm = window.confirm("Send a push notification to your followers about this coupon. Push notifications are normally $5/push, but are currently free during the trial period.")

    // if (confirm) {
    //   console.log("yes confirmed to push");

    // }

    const existingPublishedAt = couponData?.published_at || null;

    const stagedImageIds = [];
    const stagedImages = (formData.productImages || []).filter(Boolean);


    for (let i = 0; i < stagedImages.length; i++) {
      const src = stagedImages[i];

      const slotId = i + 1;

      if (!src.startsWith('data:')) continue;
      if (couponImages[slotId]?.s3_id > 0) continue;

      const blob = dataUrlToBlob(src);
      const res = await dispatchAsAsync(
        imgActions.uploadCouponImage(slotId, 0, slotId, blob),
        dispatch,
      );

      if (res?.error) {
        toast.error(`Image ${i + 1} upload failed. Please try again.`);
        return;
      }
      if (res?.id > 0) {
        stagedImageIds.push(res.id);
      }
    }


    const existingIds = collectCouponImageIds();
    const allImageIds = [...new Set([...existingIds, ...stagedImageIds])];

    const finalPayload = {
      ...incomingPayload,
      coupon_images: allImageIds,
      state: isDraft ? COUPON_STATES.DRAFT : COUPON_STATES.PUBLISHED,
      published_at: isDraft ? null : (existingPublishedAt || new Date().toISOString()),
    };

    const STRIP_KEYS = ['cities', 'created_at', 'is_affiliate_or_flyer', 'business'];
    STRIP_KEYS.forEach(k => { if (k in finalPayload) delete finalPayload[k]; });

    const couponIdToUpdate = effectiveEditCouponId || couponData?.id || null;

    const payloadWithId =
      effectiveIsEditMode && couponIdToUpdate
        ? { ...finalPayload, id: couponIdToUpdate }
        : finalPayload;

    const actionToDispatch =
      effectiveIsEditMode && couponIdToUpdate
        ? cbActions.editCoupon(couponIdToUpdate, payloadWithId)
        : cbActions.createCoupon(COUPON_TECHNICAL_KEY, payloadWithId);

    // console.log("incoming", incomingPayload)
    // console.log("final payload", finalPayload)
    const result = await dispatchAsAsync(actionToDispatch, dispatch);
    console.log('EDIT RESULT', JSON.stringify(result), JSON.stringify(payloadWithId));
    // const result = { success: true }



    // AFTER
    if (!result || result.success) {

      toast.success(isDraft ? 'Draft saved!' : 'Coupon published successfully!');

           // ── Clean up everything: storage, dirty flag ──────────────────────────
      finalizeWizard();



      const newCouponId = store.getState().couponBuilder?.[COUPON_TECHNICAL_KEY]?.data?.id;
      // const newCouponId=5;
      if (result.success) {
        setSendNotificationData({
          coupon: incomingPayload,
          businessInfo,
          couponId: newCouponId,
        })
      }
      const confirm = (!effectiveIsEditMode && !isDraft) ? await confirmPayment() : false;
      
      // ── Send push notifications to followers on new publish only ─────────
      // log effectiveIsEditMode and isDraft for clarity in case of future bugs around this logic
      if (!effectiveIsEditMode && !isDraft && confirm) {
        try {
      
          // const { title, description } = incomingPayload;

          // const type = incomingPayload.type || incomingPayload.couponType;

          // const value =
          //   incomingPayload.percentage_off ??
          //   incomingPayload.amount_off ??
          //   incomingPayload.points_back ??
          //   incomingPayload.discountValue;

          // let content;

          // if (type === 'percentage_off' || type === 'percentage') {
          //   content = `${value}% off!`;
          // } else if (type === 'amount_off' || type === 'dollar') {
          //   content = `$${value} off!`;
          // } else if (type === 'points_back' || type === 'points') {
          //   content = `${value} point(s) back!`;
          // } else {
          //   content = 'New coupon available!';
          // }

          // dispatch(notificationActions.sendNotification({
          //   business_id: businessInfo?.id,
          //   title: `${businessInfo?.name}: ${content}`,
          //   body: `${title}: ${description}`,
          //   coupon_id: String(newCouponId || ''),
          // }));
          sendNotification({
            dispatch,
            coupon: incomingPayload,
            businessInfo,
            couponId: newCouponId,
          });

        } catch (err) {
          console.error('Failed to send notifications:', err);
        }
      }
      // if (!effectiveIsEditMode && !isDraft) {
      //   try {

      //     // Dispatch the saga action, then wait for the store to reflect the
      //     // result using a one-shot subscription — no polling, no intervals.
      //     const liveFollowerState = await new Promise((resolve, reject) => {
      //       let unsubscribe;

      //       unsubscribe = store.subscribe(() => {
      //         const state = store.getState().followers;
      //         // Saga sets fetching=false when done — either success or failure
      //         if (!state.fetching && state.fetched) {
      //           unsubscribe();
      //           resolve(state);
      //         } else if (!state.fetching && state.error) {
      //           unsubscribe();
      //           reject(new Error(state.error));
      //         }
      //       });

      //       // Fire the saga AFTER subscribing to avoid missing the update
      //       dispatch(followerActions.getBusinessFollowers({ business_id: businessInfo?.id }));
      //     });

      //     const followers = liveFollowerState?.data;

      //     if (followers?.firebase_fcm?.length) {
      //       const { title, description } = incomingPayload;

      //       // Page 5 submits using API field names (type, percentage_off, amount_off,
      //       // points_back) — not the wizard-style couponType/discountValue keys.
      //       // We handle both so this works regardless of how the payload is shaped.
      //       const type = incomingPayload.type || incomingPayload.couponType;
      //       const value =
      //         incomingPayload.percentage_off ??
      //         incomingPayload.amount_off ??
      //         incomingPayload.points_back ??
      //         incomingPayload.discountValue;

      //       let content;
      //       if (type === 'percentage_off' || type === 'percentage') content = `${value}% off!`;
      //       else if (type === 'amount_off'  || type === 'dollar')   content = `$${value} off!`;
      //       else if (type === 'points_back' || type === 'points')   content = `${value} point(s) back!`;
      //       else content = 'New coupon available!';

      //       console.log("followers", followers);

      //       // log coupon ID and business ID for debugging

      //       dispatch(notificationActions.sendNotification({
      //         tokens: followers.firebase_fcm,
      //         title: `${businessInfo?.name}: ${content}`,
      //         body: `${title}: ${description}`,
      //         image: incomingPayload.coupon_images?.[0] ? String(incomingPayload.coupon_images[0]) : '',
      //         data: {
      //           coupon_id: String(newCouponId || ''),
      //           business_id: String(businessInfo?.id || ''),
      //         },
      //       }));
      //     } else {
      //       console.log('No FCM tokens found, skipping notification dispatch.');
      //     }
      //   } catch (err) {
      //     console.error('Failed to send notifications:', err);
      //   }
      // }

 

      setFormData({});
      setStep(1);
      navigate('/coupons');

      return null;
    }

    toast.error(formatErrorMessage(result.error || result));
    return result;
  };

  const isDev = process.env.NODE_ENV === 'development';

  // ── Reset helper ──────────────────────────────────────────────────────────
  const handleReset = () => {
    clearAllWizardStorage(STORAGE_KEY);
    try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch { }
    dispatch(imgActions.clearCouponImagesState());
    setFormData({});
    setIsDirty(false);
    setStep(1);
  };

  const handleModalSave = () => {
    setIsModalOpen(false);

    if (paymentResolverRef.current) {
      paymentResolverRef.current(true); // ✅ continue flow
      paymentResolverRef.current = null;
    }
  }

  const handleModalClose = () => {
    setIsModalOpen(false);

    if (paymentResolverRef.current) {
      paymentResolverRef.current(false); // ❌ stop flow
      paymentResolverRef.current = null;
    }
  }

  return (
    <>
      <AuthProtected />

      {/* ── Unsaved changes confirmation modal ─────────────────────────── */}
      <UnsavedChangesModal
        isOpen={showExitModal}
        onStay={handleStay}
        onLeave={handleLeave}
      />

      {/* {isDev && (
        <div style={{ margin: '1rem 0', padding: '0 1rem' }}>
          <label>
            🔧 Jump to Page:{' '}
            <select
              value={step}
              onChange={e => setStep(Number(e.target.value))}
            >
              <option value={1}>1 - Define Goals</option>
              <option value={2}>2 - Build Your Deal</option>
              <option value={3}>3 - Offer Impact</option>
              <option value={4}>4 - Recommended Coupons</option>
              <option value={5}>5 - Design & Publish</option>
            </select>
          </label>
        </div>
      )} */}

      {/* <button
        onClick={handleReset}
        style={{
          marginBottom: '15px',
          padding: '8px 14px',
          background: '#eee',
          borderRadius: '6px',
        }}
      >
        🔄 Reset & Start Fresh
      </button> */}

      {!isFormDataReady && <div>Loading coupon...</div>}

      {/* <PaymentModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalSave}
        productCode={products.PUSH_NOTIFICATIONS_PROD_CODE}
        message={'Would you like to send a push notification to your followers about this coupon?'}
      /> */}
      <SendCouponNotificationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalSave}
        productCode={products.PUSH_NOTIFICATIONS_PROD_CODE}
        message={'Would you like to send a push notification to your followers about this coupon?'}
        couponNotificationData={sendNotificationData}
      />

      {(() => {
        switch (step) {
          case 1:
            return (
              <Page1_Goals
                selectedGoal={formData.goalKey}
                onNext={data => {
                  if (data?.goDirectToEdit) {
                    const province = businessInfo?.province || businessInfo?.state || 'NS';
                    const defaultTaxes = getDefaultTaxes({ province, price: 0 });
                    const noGoalFormData = {
                      goalKey: undefined,
                      startedFromNoGoalFlow: true,
                      startFromScratch: true,
                      productTaxes: defaultTaxes,
                    };
                    // BUG-022 FIX: saveWizard must be called here so isDirty becomes
                    // true and the beforeunload guard is registered. Previously this
                    // branch only called setFormData/setStep, leaving isDirty=false
                    // and allowing users to navigate away from Page 5 without any
                    // warning.
                    // BUG-FIX: merge onto prior formData instead of replacing it —
                    // replacing was wiping out anything already entered (category,
                    // product info, etc.) whenever this branch fired.
                    const mergedNoGoalFormData = { ...formData, ...noGoalFormData };
                    saveWizard(mergedNoGoalFormData);
                    setFormData(mergedNoGoalFormData);
                    setStep(5);
                    return;
                  }

                  // BUG-FIX: previously this replaced formData outright ("Only the
                  // chosen goal travels forward — nothing else"), which wiped out
                  // anything already entered on Page 2+ (category, product info,
                  // images, etc.) any time the user returned to Page 1 and picked
                  // a goal again — even the same goal. Merge instead.
                  const fresh = {
                    goalKey: data.goalKey,
                    startFromScratch: false,
                    startedFromNoGoalFlow: false,
                  };
                  const mergedFresh = { ...formData, ...fresh };
                  saveWizard(mergedFresh);
                  setFormData(mergedFresh);
                  setStep(2);
                }}
              />
            );

          case 2:
            return (
              <Page2_BuildYourDeal
                goal={formData.goalKey}
                businessProvince={normProv(businessInfo?.province)}
                initialValues={{
                  audience: formData.audience,
                  exclusiveTo: formData.exclusiveTo,
                  itemType: formData.itemType,
                  product: {
                    title: formData.productTitle || formData.itemName || '',
                    description: formData.productDescription || formData.itemDescription || '',
                    price: formData.productPrice,
                    taxConfirmed: typeof formData.taxConfirmed === 'boolean' ? formData.taxConfirmed : true,
                    category: formData.category,
                    subcategories: formData.subcategories,
                    complements: formData.complements,
                    images: formData.productImages,
                    imageUrl: formData.productImageUrl,
                    taxes: formData.productTaxes,
                  },
                  constraints: formData.constraints,
                  repeatBusiness: formData.repeatBusiness,
                  slowPeriods: formData.slowPeriods,
                  availability: formData.availability,
                }}
                allowedImageSlots={businessInfo?.coupon_image_slot}
                isPremiumUser={Boolean(businessInfo?.is_premium || businessInfo?.plan === 'premium')}
                onNext={data => {
                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      audience: data.audience,
                      exclusiveTo: data.exclusiveTo,
                      itemType: data.itemType,
                      productTitle: data.product?.title,
                      productDescription: data.product?.description,
                      productPrice: data.product?.price,
                      taxConfirmed: data.product?.taxConfirmed,
                      itemName: data.product?.title ?? prev.itemName,
                      itemDescription: data.product?.description ?? prev.itemDescription,
                      category: data.product?.category,
                      subcategories: data.product?.subcategories || [],
                      complements: data.product?.complements,
                      productImages: data.product?.images || [],
                      productTaxes: data.product?.taxes ? { ...data.product.taxes } : prev.productTaxes || {},
                      productImageUrl: data.product?.imageUrl || (data.product?.images?.[0]) || prev.productImageUrl || null,
                      constraints: data.constraints,
                      repeatBusiness: data.repeatBusiness,
                      slowPeriods: data.slowPeriods,
                      availability: data.availability,
                      minSpend: data.constraints?.minSpend ?? prev.minSpend,
                      purchaseQty: data.constraints?.purchaseQty ?? prev.purchaseQty,
                    };
                    saveWizard(updated);
                    return updated;
                  });
                  setStep(3);
                }}
                onBack={() => setStep(1)}
              />
            );

          case 3:
            return (
              <Page3_OfferImpact
                goal={formData.goalKey || 'No Goal'}
                scope={scopeFromItemType(formData.itemType)}
                itemName={formData.productTitle || ''}
                itemPrice={formData.productPrice || ''}
                itemDescription={formData.productDescription || ''}
                selectedImpact={formData.offerImpact}
                onNext={data => {
                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      offerImpact: data.offerImpact,
                      offerImpactSpeed: data.speed,
                      productTitle: data.itemName ?? formData.productTitle,
                      productPrice: data.itemPrice ?? formData.productPrice,
                      productDescription: data.itemDescription ?? formData.productDescription,
                      heroImagePreview: data.heroImagePreview ?? formData.heroImagePreview,
                      itemName: data.itemName ?? prev.itemName,
                      itemDescription: data.itemDescription ?? prev.itemDescription,
                    };
                    saveWizard(updated);
                    return updated;
                  });
                  setStep(4);
                }}
                onBack={() => setStep(2)}
              />
            );

          case 4: {
            if (effectiveIsEditMode) {
              setStep(5);
              return null;
            }
            const normalizedGoal = normalizeGoalForP4(formData.goalKey);
            const normalizedSpeed = normalizeImpactToSpeed(formData.offerImpactSpeed);
            return (
              <Page4_RecommendedCoupons
                goal={normalizedGoal}
                speed={normalizedSpeed}
                scope={scopeFromItemType(formData.itemType)}
                item={{
                  name: formData.productTitle || '',
                  price: formData.productPrice || 0,
                  description: formData.productDescription || '',
                  primaryImage: formData.productImageUrl || null,
                  images: formData.productImages || [],
                  category: formData.category,
                  categoryId: formData.category?.id ?? null,
                  subcategories: formData.subcategories || [],
                }}
                exclusivity={formData.exclusiveTo || ''}
                selectedTemplate={formData.recommendedTemplate}
                audience={formData.audience || []}
                repeatBusiness={formData.repeatBusiness}
                slowPeriods={formData.slowPeriods}
                constraints={formData.constraints}
                onSubmit={(payload) => handleFinalSubmit(payload, false)}
                onSaveDraft={(payload) => handleFinalSubmit(payload, true)}
                onNext={({ couponPayload, recommendedTemplate, startFromScratch }) => {
                  const noPayload = !couponPayload || startFromScratch;
                  const normalizedType = noPayload ? '' : couponPayload?.couponType || '';
                  const normalizedValue = noPayload ? '' : couponPayload?.discountValue ?? '';

                  let computedExpiryDate = null;
                  const days = couponPayload?.expiryDays ?? recommendedTemplate?.expiryDays ?? null;
                  if (days && Number(days) > 0) {
                    const d = new Date();
                    d.setDate(d.getDate() + Number(days));
                    computedExpiryDate = d;
                  }

                  setFormData(prev => {
                    const updated = {
                      ...prev,
                      recommendedTemplate: recommendedTemplate || (noPayload ? 'empty' : undefined),
                      title: noPayload ? '' : couponPayload?.title || '',
                      description: noPayload ? '' : couponPayload?.description || '',
                      couponType: normalizedType,
                      discountValue: normalizedValue,
                      expiryDays: days || prev.expiryDays,
                      expiryType: noPayload
                        ? 'date'
                        : couponPayload?.expiryType === 'amount'
                          ? 'quantity'
                          : 'date',
                      goalKey: formData.goalKey,
                      productTitle: formData.productTitle,
                      productPrice: formData.productPrice,
                      productDescription: formData.productDescription,
                      itemType: formData.itemType,
                      heroImagePreview: formData.productImageUrl || prev.heroImagePreview || null,
                    };

                    if (!noPayload) {
                      updated.expiryDate = computedExpiryDate;
                      updated.totalQuantity = couponPayload?.totalCoupons || '';
                      updated.terms = couponPayload?.terms ?? prev.terms ?? '';

                      const limitType = couponPayload?.limitType ?? null;
                      const limitQty = couponPayload?.limitQty ?? null;

                      const ex = couponPayload?.meta?.exclusivity || couponPayload?.exclusivity || prev.exclusiveTo || '';
                      updated.exclusiveTo = ex;
                      updated.customerType = ex ? [ex] : [];

                      if (limitType === 'unlimited') {
                        updated.perUserLimitType = 'unlimited';
                        updated.perUserLimitCount = '';
                      } else if (limitType === 'custom' && Number(limitQty) > 0) {
                        updated.perUserLimitType = Number(limitQty) === 1 ? 'oneTime' : 'multiple';
                        updated.perUserLimitCount = Number(limitQty) === 1 ? '' : Number(limitQty);
                      } else {
                        updated.perUserLimitType = prev.perUserLimitType ?? 'unlimited';
                        updated.perUserLimitCount = prev.perUserLimitCount ?? '';
                      }

                      updated.redemptionType = couponPayload?.redemptionType || prev.redemptionType || 'auto';
                      updated.reservationHours = couponPayload?.reservationHours ?? prev.reservationHours ?? 4;
                      updated.redeemableWithPoints = couponPayload?.redeemableWithPoints ?? prev.redeemableWithPoints ?? false;

                      if (couponPayload?.minSpend !== undefined) updated.minSpend = couponPayload.minSpend;
                      if (couponPayload?.purchaseQty !== undefined) updated.purchaseQty = couponPayload.purchaseQty;
                    }

                    saveWizard(updated);
                    return updated;
                  });
                  setStep(5);
                }}
                onBack={() => setStep(3)}
              />
            );
          }

          case 5:
            return (
              <Page5_DesignCoupon
                initialValues={{
                  startFromScratch: !!formData.startFromScratch,
                  title: formData.title,
                  description: formData.description,
                  couponType: formData.couponType || '',
                  discountValue: formData.discountValue ?? 0,
                  heroImagePreview: formData.heroImagePreview || formData.productImageUrl || '',
                  productImages: formData.productImages || [],
                  isEditMode: effectiveIsEditMode,

                  // Context data
                  is_exclusive_followers: couponData?.is_exclusive_followers,
                  is_exclusive_paid: couponData?.is_exclusive_paid,

                  businessName: formData.businessName,
                  city: formData.city,
                  goalKey: formData.goalKey || 'general',
                  itemType: formData.itemType === 'all' || formData.itemType === 'storewide' ? 'storewide' : formData.itemType === 'category' ? 'category' : 'individual',
                  scope: scopeFromItemType(formData.itemType),

                  itemPrice: formData.productPrice || '',
                  itemName: formData.itemName || formData.productTitle || couponData?.item_name || '',
                  itemDescription: formData.itemDescription || formData.productDescription || couponData?.item_description || '',
                  itemImageUrl: formData.productImageUrl || '',

                  audience: formData.audience || [],
                  exclusiveTo: formData.exclusiveTo || '',
                  desiredImpact: formData.offerImpact || 'Steady',

                  // Expiry & Limits (merged from Page 6 logic)
                  expiryType: formData.expiryType || 'date',
                  expiryDate: formData.expiryDate instanceof Date ? formData.expiryDate : formData.expiryDate ? new Date(formData.expiryDate) : null,
                  totalQuantity: formData.totalQuantity || '',
                  minSpend: formData.minSpend ?? null,
                  purchaseQty: formData.purchaseQty ?? null,

                  slowDays: formData.slowPeriods || [],
                  slowTimes: formData.availability || [],
                  promotionName: formData.promotionName || '',

                  // Advanced Defaults (New fields from Page 6)
                  reservationHours: formData.reservationHours ?? 4,
                  perUserLimitType: formData.perUserLimitType || 'unlimited',
                  perUserLimitCount: formData.perUserLimitCount || '',
                  redemptionType: formData.redemptionType || 'auto',
                  redeemableWithPoints: formData.redeemableWithPoints || false,
                  terms: formData.terms || '',
                  customerType: formData.customerType || [],

                  // Categories
                  category: formData.category,
                  subcategories: formData.subcategories,
                }}
                onSubmit={(payload) => handleFinalSubmit(payload, false)}
                // onSubmit={(payload) => setIsModalOpen(true)}
                onSaveDraft={(payload) => handleFinalSubmit(payload, true)}
                onBack={() => {
                  if (formData.startedFromNoGoalFlow) setStep(1);
                  else setStep(4);
                }}
                isPremiumUser={user?.is_premium}
                pointsBalance={user?.wallet?.points || 0}
              />
            );

          default:
            return <div>Done</div>;
        }
      })()}
    </>
  );
};

export default CouponBuilderV1;

// import React, { useState, useEffect } from 'react';
// import { useDispatch, useSelector } from 'react-redux';
// import Page1_Goals from './Page1_Goals';
// import Page2_BuildYourDeal from './Page2_BuildYourDeal';
// import Page3_OfferImpact from './Page3_OfferImpact';
// import Page4_RecommendedCoupons from './Page4_RecommendedCoupons';
// import Page5_DesignCoupon from './Page5_DesignCoupon';
// // Page 6 is removed
// import { getDefaultTaxes } from 'utils/taxDefaults';
// import {
//   actions as cbActions,
//   duck as cbDuck,
// } from 'coupon-common/src/modules/couponBuilder';
// import { actions as imgActions } from 'coupon-common/src/modules/couponImages';
// import { actions as bizActions } from 'coupon-common/src/modules/businessInfo';
// import { dispatchAsAsync } from 'coupon-common/src/utils';
// import { COUPON_STATES } from 'coupon-common';
// import { useHistory, useLocation, useParams } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import AuthProtected from 'containers/AuthProtected/AuthProtected';

// const LOCAL_STORAGE_KEY_BASE = 'coupon_builder_v2_data';
// const LOCAL_STORAGE_KEY = LOCAL_STORAGE_KEY_BASE;
// const COUPON_TECHNICAL_KEY = 'newCouponID';
// const TOTAL_STEPS = 5;


// const dataUrlToBlob = (dataUrl) => {
//   const [header, data] = dataUrl.split(',');
//   const mime = header.match(/:(.*?);/)[1];
//   const bytes = atob(data);
//   const arr = new Uint8Array(bytes.length);
//   for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
//   return new Blob([arr], { type: mime });
// };


// const normalizeImpactToSpeed = key => {
//   if (!key) return 'Steady';
//   const k = String(key).toLowerCase().trim();
//   if (['steady', 'steady_growth', 'balanced', '1'].includes(k)) return 'Steady';
//   if (['quick', 'quick_boost', 'accelerated', '2'].includes(k)) return 'Quick';
//   if (['max', 'maximum_impact', 'aggressive', '3'].includes(k)) return 'Max';
//   return 'Steady';
// };

// const scopeFromItemType = itemType =>
//   itemType === 'individual'
//     ? 'Individual Item'
//     : itemType === 'category'
//     ? 'Product Line'
//     : 'Storewide Promotion';

// // Normalize any goal label variants from Page 1 into Page 4’s compact set.
// const normalizeGoalForP4 = goalKey => {
//   if (!goalKey) return 'No Goal';
//   const g = String(goalKey).toLowerCase();
//   if (g.includes('more customers') || g.includes('attract')) return 'New Customers';
//   if (g.includes('sell')) return 'Sell More';
//   if (g.includes('repeat')) return 'Repeat Business';
//   if (g.includes('slow') || g.includes('off-peak')) return 'Slow Periods';
//   if (g.includes('special')) return 'Special Promotion';
//   return 'No Goal';
// };

// const CouponBuilderV1 = ({ user, startAtStep, isEditMode, editCouponId }) => {
//   const dispatch = useDispatch();
//   const { id: routeCouponId } = useParams();
//   const navigate = useNavigate();
//   const location = useLocation();

//   const effectiveEditCouponId = editCouponId || routeCouponId || null;
//   const searchParams = new URLSearchParams(location.search);
//   const urlMode = (searchParams.get('mode') || '').toLowerCase();

//   const effectiveIsEditMode =
//     typeof isEditMode === 'boolean'
//       ? isEditMode
//       : urlMode === 'edit' && !!effectiveEditCouponId;

//   const businessInfo = useSelector(s => s.businessInfo?.data);
//   const businessKey =
//     businessInfo?.id || businessInfo?.business_id || user?.id || 'unknown';

//   const STORAGE_KEY = `${LOCAL_STORAGE_KEY_BASE}:${businessKey}`;
//   const couponImages = useSelector(s => s.couponImages || {});

//   const couponBuilderState = useSelector(s => s.couponBuilder || {});
//   const couponEntry =
//     (effectiveEditCouponId && couponBuilderState[effectiveEditCouponId]) ||
//     cbDuck.INITIAL_COUPON_ITEM_STATE;

//   const { data: couponData } = couponEntry;

//   const [step, setStep] = useState(1);
//   const [formData, setFormData] = useState({});

//   const isFormDataReady =
//     !effectiveIsEditMode ||
//     (effectiveIsEditMode && !!couponData && !!couponData.id);

//   // Convert serialized fields back to runtime types
//   const normalizeWizard = obj => {
//     const out = { ...(obj || {}) };
//     if (out.expiryDate) {
//       const d = new Date(out.expiryDate);
//       out.expiryDate = Number.isNaN(d.getTime()) ? null : d;
//     }
//     return out;
//   };

//   useEffect(() => {
//     if (!businessKey || businessKey === 'unknown') return;
//     const stored = localStorage.getItem(STORAGE_KEY);
//     if (stored) {
//       const parsed = JSON.parse(stored);
//       setFormData(normalizeWizard(parsed));
//     } else {
//       setFormData({});
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [businessKey]);

//   const saveWizard = data => {
//     localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
//   };

//   // Cleanup legacy keys
//   useEffect(() => {
//     try {
//       localStorage.removeItem('coupon_builder_v2_data');
//     } catch {}
//   }, []);

//   // Load business info
//   useEffect(() => {
//     dispatch(bizActions.getInfo(COUPON_TECHNICAL_KEY));
//   }, [dispatch]);

//   // Initialize coupon image slots
//   useEffect(() => {
//     const slots =
//       (businessInfo?.coupon_image_slot && Number(businessInfo.coupon_image_slot)) ||
//       (user?.is_premium ? 3 : 1);
//     if (slots) {
//       const initialImagesPayload =
//         effectiveIsEditMode && couponData ? couponData : {};
//       dispatch(imgActions.loadCouponImagesState(slots, initialImagesPayload));
//     }
//   }, [dispatch, businessInfo, user?.is_premium, effectiveIsEditMode, couponData]);

//   // Start step logic
//   useEffect(() => {
//     let initial = 0;
//     if (typeof startAtStep === 'number' && startAtStep > 0) {
//       initial = startAtStep;
//     }
//     if (!initial) {
//       const params = new URLSearchParams(location.search);
//       const mode = (params.get('mode') || '').toLowerCase();
//       const urlStep = Number(params.get('step') || 0);
//       if (mode === 'edit' && urlStep > 0) initial = urlStep;
//     }
//     if (!initial) {
//       const forced = Number(localStorage.getItem('cbv2_forceStep') || 0);
//       if (forced > 0) initial = forced;
//     }
//     if (initial > 0) setStep(initial);
//     try {
//       localStorage.removeItem('cbv2_forceStep');
//     } catch {}
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   useEffect(() => {
//     if (!effectiveIsEditMode || !effectiveEditCouponId) return;
//     dispatch(cbActions.getCoupon(effectiveEditCouponId));
//   }, [dispatch, effectiveIsEditMode, effectiveEditCouponId]);

//   // PREFILL FORM DATA ON EDIT
//   useEffect(() => {
//     if (!effectiveIsEditMode || !effectiveEditCouponId) return;
//     if (!couponData || !couponData.id) return;

//     localStorage.removeItem(LOCAL_STORAGE_KEY);
//     const c = couponData;

//     const prefill = {
//       title: c.title || '',
//       description: c.description || '',
//       couponType:
//         c.type === 'percentage_off' ? 'percentage' : c.type === 'amount_off' ? 'dollar' : 'points',
//       discountValue:
//         c.type === 'percentage_off'
//           ? Number(c.percentage_off || 0)
//           : c.type === 'amount_off'
//           ? Number(c.amount_off || 0)
//           : Number(c.points_back || 0),
//       productPrice: c.price ?? c.product_price ?? c.product?.price ?? '',
//       itemName: c.item_name || '',
//       itemDescription: c.item_description || '',
//       productTitle: c.item_name || '',
//       productDescription: c.item_description || '',
//       itemPrice: c.price ?? c.product_price ?? c.product?.price ?? '',
//       itemType: c.item_type === 'all' ? 'storewide' : c.item_type || 'individual',

//       // Page 6 fields (Merged into Page 5)
//     expiryType: c.expiry_type === 'amount_limit' ? 'quantity' : 'date',  // normalize to Page5 format
//     totalQuantity: c.expiration_quantity || '',  // preserve quantity value
//     exclusiveTo: c.is_exclusive_paid
//       ? 'premium'
//       : c.is_exclusive_followers
//       ? 'followers'
//       : '',
//     customerType: c.is_exclusive_paid    // ← ADD THIS - binds to the actual form field
//       ? ['premium']
//       : c.is_exclusive_followers
//       ? ['followers']
//   : [],
//       perUserLimitType:
//         c.user_limit === 1
//           ? 'oneTime'
//           : c.user_limit > 1
//           ? 'multiple'
//           : 'unlimited',
//       perUserLimitCount: c.user_limit > 1 ? c.user_limit : '',
//       redemptionType: c.auto_accept ? 'auto' : 'manual',
//       redeemableWithPoints: c.payment_by_points || false,
//       reservationHours: c.reserved_hours || 4,
//       terms: c.terms_and_conditions || c.terms || '',
//       exclusiveTo: c.is_exclusive_paid
//         ? 'premium'
//         : c.is_exclusive_followers
//         ? 'followers'
//         : '',
//       category: c.category || null,
//       subcategories: c.subcategories || [],
//       heroImagePreview: c.image_url || '',
//     };

//     setFormData(prev => ({ ...prev, ...prefill }));
//   }, [effectiveIsEditMode, effectiveEditCouponId, couponData]);

//   const safeMerge = (prev, next) => normalizeWizard({ ...prev, ...next });

//   // Collect S3 image IDs
//   const collectCouponImageIds = () =>
//     Object.keys(couponImages)
//       .map(k => (couponImages[k]?.s3_id > 0 ? couponImages[k].s3_id : null))
//       .filter(Boolean);

//   const formatErrorMessage = errorObj => {
//     if (!errorObj || typeof errorObj !== 'object') {
//       return 'An unexpected error occurred.';
//     }
//     const parts = [];
//     for (const [field, errs] of Object.entries(errorObj)) {
//       if (Array.isArray(errs)) {
//         errs.forEach(msg => {
//           parts.push(`${field === 'non_field_errors' ? 'Error' : field}: ${msg}`);
//         });
//       } else {
//         parts.push(`${field}: ${errs}`);
//       }
//     }
//     return parts.join('\n');
//   };

//   /**
//    * DIRECT DISPATCH FUNCTION
//    * Page 5 now generates a complete payload. We just need to add
//    * image IDs, handle state (draft vs published), and handle IDs for editing.
//    */
// const handleFinalSubmit = async (incomingPayload, isDraft = false) => {
//   const existingPublishedAt = couponData?.published_at || null;

//   // 🔍 LOG 1: What images did Page 2 store?
//   console.log('1. formData.productImages:', formData.productImages);

//   // 🔍 LOG 2: What is the incoming payload's coupon_images already?
//   console.log('2. incomingPayload.coupon_images:', incomingPayload.coupon_images);

//   // 🔍 LOG 3: What does Redux have right now?
//   console.log('3. couponImages (Redux):', couponImages);
//   console.log('3b. collectCouponImageIds():', collectCouponImageIds());

//   const stagedImageIds = [];
//   const stagedImages = (formData.productImages || []).filter(Boolean);
//   console.log('4. stagedImages to process:', stagedImages.length, stagedImages.map(s => s.substring(0, 30)));

//   for (let i = 0; i < stagedImages.length; i++) {
//     const src = stagedImages[i];
//     console.log(`5. Image ${i}: starts with data:?`, src.startsWith('data:'));

//     const slotId = i + 1;
//     console.log(`6. Slot ${slotId} already has s3_id?`, couponImages[slotId]?.s3_id);

//     if (!src.startsWith('data:')) continue;
//     if (couponImages[slotId]?.s3_id > 0) continue;

//     const blob = dataUrlToBlob(src);
//     const res = await dispatchAsAsync(
//       imgActions.uploadCouponImage(slotId, 0, slotId, blob),
//       dispatch,
//     );

//     // 🔍 LOG 4: What did the upload return?
//     console.log(`7. Upload response for image ${i}:`, res);

//     if (res?.error) {
//       toast.error(`Image ${i + 1} upload failed. Please try again.`);
//       return;
//     }

//     if (res?.id > 0) {
//       stagedImageIds.push(res.id);
//     }
//   }

//   console.log('8. stagedImageIds collected:', stagedImageIds);
//   console.log('9. collectCouponImageIds() after uploads:', collectCouponImageIds());

//   const existingIds = collectCouponImageIds();
//   const allImageIds = [...new Set([...existingIds, ...stagedImageIds])];
//   console.log('10. FINAL allImageIds going into payload:', allImageIds);
// const finalPayload = {
//   ...incomingPayload,
//   coupon_images: allImageIds, // ✅ uses merged list, not stale selector alone
//   state: isDraft ? COUPON_STATES.DRAFT : COUPON_STATES.PUBLISHED,
//   published_at: isDraft ? null : (existingPublishedAt || new Date().toISOString()),
// };

//     // 2. Clean up restricted keys if they slipped in
//     const STRIP_KEYS = [
//       'cities',
//       'created_at',
//       'is_affiliate_or_flyer',
//       'business',
//     ];
//     STRIP_KEYS.forEach(k => {
//       if (k in finalPayload) delete finalPayload[k];
//     });

//     const couponIdToUpdate = effectiveEditCouponId || couponData?.id || null;

//     // 3. Attach ID if editing
//     const payloadWithId =
//       effectiveIsEditMode && couponIdToUpdate
//         ? { ...finalPayload, id: couponIdToUpdate }
//         : finalPayload;

//     console.log('FINAL API PAYLOAD:', payloadWithId);

//     // 4. Dispatch Action
//     const actionToDispatch =
//       effectiveIsEditMode && couponIdToUpdate
//         ? cbActions.editCoupon(couponIdToUpdate, payloadWithId)
//         : cbActions.createCoupon(COUPON_TECHNICAL_KEY, payloadWithId);

//     const result = await dispatchAsAsync(actionToDispatch, dispatch);

//     // 5. Handle Result
//     if (!result || result.success) {
//       toast.success(isDraft ? 'Draft saved!' : 'Coupon published successfully!');

//       // Cleanup storage
//       localStorage.removeItem(LOCAL_STORAGE_KEY);
//       localStorage.removeItem('cbv2_editMode');
//       localStorage.removeItem('cbv2_editCouponId');
//       localStorage.removeItem('cbv2_forceStep');

//       if (!isDraft) {
//         setFormData({});
//         setStep(1);
//         history.push('/coupons');
//       }
//       return null;
//     }

//     toast.error(formatErrorMessage(result.error || result));
//     return result;
//   };

//   const isDev = process.env.NODE_ENV === 'development';

//   return (
//     <>
//       <AuthProtected />

//       {isDev && (
//         <div style={{ margin: '1rem 0', padding: '0 1rem' }}>
//           <label>
//             🔧 Jump to Page:{' '}
//             <select
//               value={step}
//               onChange={e => setStep(Number(e.target.value))}
//             >
//               <option value={1}>1 - Define Goals</option>
//               <option value={2}>2 - Build Your Deal</option>
//               <option value={3}>3 - Offer Impact</option>
//               <option value={4}>4 - Recommended Coupons</option>
//               <option value={5}>5 - Design & Publish</option>
//             </select>
//           </label>
//         </div>
//       )}

//       <button
//         onClick={() => {
//           localStorage.removeItem(LOCAL_STORAGE_KEY);
//           localStorage.removeItem('cbv2_editMode');
//           localStorage.removeItem('cbv2_editCouponId');
//           localStorage.removeItem('cbv2_forceStep');
//           dispatch(imgActions.clearCouponImagesState());
//           setFormData({});
//           setStep(1);
//         }}
//         style={{
//           marginBottom: '15px',
//           padding: '8px 14px',
//           background: '#eee',
//           borderRadius: '6px',
//         }}
//       >
//         🔄 Reset & Start Fresh
//       </button>

//       {!isFormDataReady && <div>Loading coupon...</div>}

//       {(() => {
//         switch (step) {
//           case 1:
//             return (
//               <Page1_Goals
//                 selectedGoal={formData.goalKey}
//                 onNext={data => {
//                   if (data?.goDirectToEdit) {
//                     const province = businessInfo?.province || businessInfo?.state || 'NS';
//                     const defaultTaxes = getDefaultTaxes({ province, price: 0 });
//                     setFormData({
//                       goalKey: undefined,
//                       startedFromNoGoalFlow: true,
//                       startFromScratch: true,
//                       productTaxes: defaultTaxes,
//                     });
//                     setStep(5);
//                     return;
//                   }

//                   // Only the chosen goal travels forward — nothing else.
//                   const fresh = {
//                     goalKey: data.goalKey,
//                     startFromScratch: false,
//                     startedFromNoGoalFlow: false,
//                   };
//                   saveWizard(fresh);
//                   setFormData(fresh);
//                   setStep(2);
//                 }}
//               />
//             );

//           case 2:
//             return (
//               <Page2_BuildYourDeal
//                 goal={formData.goalKey}
//                 initialValues={{
//                   audience: formData.audience,
//                   exclusiveTo: formData.exclusiveTo,
//                   itemType: formData.itemType,
//                   product: {
//                     title: formData.productTitle || formData.itemName || '',
//                     description: formData.productDescription || formData.itemDescription || '',
//                     price: formData.productPrice,
//                     taxConfirmed: typeof formData.taxConfirmed === 'boolean' ? formData.taxConfirmed : true,
//                     category: formData.category,
//                     subcategories: formData.subcategories,
//                     complements: formData.complements,
//                     images: formData.productImages,
//                     imageUrl: formData.productImageUrl,
//                     taxes: formData.productTaxes,
//                   },
//                   constraints: formData.constraints,
//                   repeatBusiness: formData.repeatBusiness,
//                   slowPeriods: formData.slowPeriods,
//                   availability: formData.availability,
//                 }}
//                 allowedImageSlots={businessInfo?.coupon_image_slot}
//                 isPremiumUser={Boolean(businessInfo?.is_premium || businessInfo?.plan === 'premium')}
//                 onNext={data => {
//                   setFormData(prev => {
//                     const updated = {
//                       ...prev,
//                       audience: data.audience,
//                       exclusiveTo: data.exclusiveTo,
//                       itemType: data.itemType,
//                       productTitle: data.product?.title,
//                       productDescription: data.product?.description,
//                       productPrice: data.product?.price,
//                       taxConfirmed: data.product?.taxConfirmed,
//                       itemName: data.product?.title ?? prev.itemName,
//                       itemDescription: data.product?.description ?? prev.itemDescription,
//                       category: data.product?.category,
//                       subcategories: data.product?.subcategories || [],
//                       complements: data.product?.complements,
//                       productImages: data.product?.images || [],
//                       productTaxes: data.product?.taxes ? { ...data.product.taxes } : prev.productTaxes || {},
//                       productImageUrl: data.product?.imageUrl || (data.product?.images?.[0]) || prev.productImageUrl || null,
//                       constraints: data.constraints,
//                       repeatBusiness: data.repeatBusiness,
//                       slowPeriods: data.slowPeriods,
//                       availability: data.availability,
//                       minSpend: data.constraints?.minSpend ?? prev.minSpend,
//                       purchaseQty: data.constraints?.purchaseQty ?? prev.purchaseQty,
//                     };
//                     saveWizard(updated);
//                     return updated;
//                   });
//                   setStep(3);
//                 }}
//                 onBack={() => setStep(1)}
//               />
//             );

//           case 3:
//             return (
//               <Page3_OfferImpact
//                 goal={formData.goalKey || 'No Goal'}
//                 scope={scopeFromItemType(formData.itemType)}
//                 itemName={formData.productTitle || ''}
//                 itemPrice={formData.productPrice || ''}
//                 itemDescription={formData.productDescription || ''}
//                 selectedImpact={formData.offerImpact}
//                 onNext={data => {
//                   setFormData(prev => {
//                     const updated = {
//                       ...prev,
//                       offerImpact: data.offerImpact,
//                       offerImpactSpeed: data.speed,
//                       productTitle: data.itemName ?? formData.productTitle,
//                       productPrice: data.itemPrice ?? formData.productPrice,
//                       productDescription: data.itemDescription ?? formData.productDescription,
//                       heroImagePreview: data.heroImagePreview ?? formData.heroImagePreview,
//                       itemName: data.itemName ?? prev.itemName,
//                       itemDescription: data.itemDescription ?? prev.itemDescription,
//                     };
//                     saveWizard(updated);
//                     return updated;
//                   });
//                   setStep(4);
//                 }}
//                 onBack={() => setStep(2)}
//               />
//             );

//           case 4: {
//             if (effectiveIsEditMode) {
//               setStep(5);
//               return null;
//             }
//             const normalizedGoal = normalizeGoalForP4(formData.goalKey);
//             const normalizedSpeed = normalizeImpactToSpeed(formData.offerImpactSpeed);
//             return (
//               <Page4_RecommendedCoupons
//                 goal={normalizedGoal}
//                 speed={normalizedSpeed}
//                 scope={scopeFromItemType(formData.itemType)}
//                 item={{
//                   name: formData.productTitle || '',
//                   price: formData.productPrice || 0,
//                   description: formData.productDescription || '',
//                   primaryImage: formData.productImageUrl || null,
//                   images: formData.productImages || [],
//                 }}
//                 exclusivity={formData.exclusiveTo || ''}
//                 selectedTemplate={formData.recommendedTemplate}
//                  audience={formData.audience || []}
//                 repeatBusiness={formData.repeatBusiness}
//                 slowPeriods={formData.slowPeriods}
//                 constraints={formData.constraints}
//                 onNext={({ couponPayload, recommendedTemplate, startFromScratch }) => {
//                   const noPayload = !couponPayload || startFromScratch;
//                   const normalizedType = noPayload ? '' : couponPayload?.couponType || '';
//                   const normalizedValue = noPayload ? '' : couponPayload?.discountValue ?? '';

//                   let computedExpiryDate = null;
//                   const days = couponPayload?.expiryDays ?? recommendedTemplate?.expiryDays ?? null;
//                   if (days && Number(days) > 0) {
//                     const d = new Date();
//                     d.setDate(d.getDate() + Number(days));
//                     computedExpiryDate = d;
//                   }

//                   setFormData(prev => {
//                     const updated = {
//                       ...prev,
//                       recommendedTemplate: recommendedTemplate || (noPayload ? 'empty' : undefined),
//                       title: noPayload ? '' : couponPayload?.title || '',
//                       description: noPayload ? '' : couponPayload?.description || '',
//                       couponType: normalizedType,
//                       discountValue: normalizedValue,
//                       expiryDays: days || prev.expiryDays,
//                       expiryType: noPayload
//                         ? 'date'
//                         : couponPayload?.expiryType === 'amount'
//                         ? 'quantity'
//                         : 'date',
//                       goalKey: formData.goalKey,
//                       productTitle: formData.productTitle,
//                       productPrice: formData.productPrice,
//                       productDescription: formData.productDescription,
//                       itemType: formData.itemType,
//                       heroImagePreview: formData.productImageUrl || prev.heroImagePreview || null,
//                     };

//                    if (!noPayload) {
//                        updated.expiryDate = computedExpiryDate;
//                        updated.totalQuantity = couponPayload?.totalCoupons || '';

//                       // ✅ TERMS: carry forward from Page 4 into wizard state
//                       updated.terms = couponPayload?.terms ?? prev.terms ?? '';

//                       // ✅ LIMITS: Page 4 sends limitType/limitQty, map them to Page 5 fields
//                       const limitType = couponPayload?.limitType ?? null;
//                       const limitQty = couponPayload?.limitQty ?? null;

//                       // ✅ EXCLUSIVITY (ALREADY EXISTS BUT WILL NOW WORK)
//                       const ex = couponPayload?.meta?.exclusivity || couponPayload?.exclusivity || prev.exclusiveTo || '';
//                       updated.exclusiveTo = ex;
//                       updated.customerType = ex ? [ex] : [];

//                       if (limitType === 'unlimited') {
//                         updated.perUserLimitType = 'unlimited';
//                         updated.perUserLimitCount = '';
//                       } else if (limitType === 'custom' && Number(limitQty) > 0) {
//                         updated.perUserLimitType = Number(limitQty) === 1 ? 'oneTime' : 'multiple';
//                         updated.perUserLimitCount = Number(limitQty) === 1 ? '' : Number(limitQty);
//                       } else {
//                         updated.perUserLimitType = prev.perUserLimitType ?? 'unlimited';
//                         updated.perUserLimitCount = prev.perUserLimitCount ?? '';
//                       }

//                       // ✅ ADD THESE NEW HANDLERS:
//                       updated.redemptionType = couponPayload?.redemptionType || prev.redemptionType || 'auto';
//                       updated.reservationHours = couponPayload?.reservationHours ?? prev.reservationHours ?? 4;
//                       updated.redeemableWithPoints = couponPayload?.redeemableWithPoints ?? prev.redeemableWithPoints ?? false;

//                       // ✅ OPTIONAL: explicit minSpend/purchaseQty
//                       if (couponPayload?.minSpend !== undefined) {
//                         updated.minSpend = couponPayload.minSpend;
//                       }
//                       if (couponPayload?.purchaseQty !== undefined) {
//                         updated.purchaseQty = couponPayload.purchaseQty;
//                       }
//                     }

//                     saveWizard(updated);
//                     return updated;
//                   });
//                   setStep(5);
//                 }}
//                 onBack={() => setStep(3)}
//               />
//             );
//           }

//           case 5:
//             return (
//               <Page5_DesignCoupon
//                 initialValues={{
//                   startFromScratch: !!formData.startFromScratch,
//                   title: formData.title,
//                   description: formData.description,
//                   couponType: formData.couponType || '',
//                   discountValue: formData.discountValue ?? 0,
//                   heroImagePreview: formData.heroImagePreview || formData.productImageUrl || '',
//                   productImages: formData.productImages || [],
//                   isEditMode: effectiveIsEditMode,


//                   // Context data
//                   businessName: formData.businessName,
//                   city: formData.city,
//                   goalKey: formData.goalKey || 'general',
//                   itemType: formData.itemType === 'all' || formData.itemType === 'storewide' ? 'storewide' : formData.itemType === 'category' ? 'category' : 'individual',
//                   scope: scopeFromItemType(formData.itemType),

//                   itemPrice: formData.productPrice || '',
//                   itemName: formData.itemName || formData.productTitle || couponData?.item_name || '',
//                   itemDescription: formData.itemDescription || formData.productDescription || couponData?.item_description || '',
//                   itemImageUrl: formData.productImageUrl || '',

//                   audience: formData.audience || [],
//                   exclusiveTo: formData.exclusiveTo || '',
//                   desiredImpact: formData.offerImpact || 'Steady',

//                   // Expiry & Limits (merged from Page 6 logic)
//                   expiryType: formData.expiryType || 'date',
//                   expiryDate: formData.expiryDate instanceof Date ? formData.expiryDate : formData.expiryDate ? new Date(formData.expiryDate) : null,
//                   totalQuantity: formData.totalQuantity || '',
//                   minSpend: formData.minSpend ?? null,
//                   purchaseQty: formData.purchaseQty ?? null,

//                   slowDays: formData.slowPeriods || [],
//                   slowTimes: formData.availability || [],
//                   promotionName: formData.promotionName || '',

//                   // Advanced Defaults (New fields from Page 6)
//                   reservationHours: formData.reservationHours ?? 4,
//                   perUserLimitType: formData.perUserLimitType || 'unlimited',
//                   perUserLimitCount: formData.perUserLimitCount || '',
//                   redemptionType: formData.redemptionType || 'auto',
//                   redeemableWithPoints: formData.redeemableWithPoints || false,
//                   terms: formData.terms || '',
//                   customerType: formData.customerType || [],

//                   // Categories
//                   category: formData.category,
//                   subcategories: formData.subcategories,
//                 }}
//                 // HANDLE PUBLISH
//                 onSubmit={(payload) => handleFinalSubmit(payload, false)}
//                 // HANDLE DRAFT
//                 onSaveDraft={(payload) => handleFinalSubmit(payload, true)}
//                 onBack={() => {
//                   if (formData.startedFromNoGoalFlow) setStep(1);
//                   else setStep(4);
//                 }}
//                 isPremiumUser={user?.is_premium}
//                 pointsBalance={user?.wallet?.points || 0}
//               />
//             );

//           default:
//             return <div>Done</div>;
//         }
//       })()}
//     </>
//   );
// };

// export default CouponBuilderV1;